import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const QuotationDetail = () => {
  const { id } = useParams();
  const [quotation, setQuotation] = useState(null);

  useEffect(() => {
    api.get(`/quotations/${id}/`).then((res) => setQuotation(res.data));
  }, [id]);

  const handleDownloadPDF = async () => {
    if (!quotation) return;
    const doc = new jsPDF();
    const themeColor = [0, 128, 128]; // Teal

    // Add Logo
    try {
      const logoUrl = process.env.PUBLIC_URL + "/rekonic-logo.jpg";
      const logoImg = await new Promise((resolve) => {
        const img = new Image();
        img.src = logoUrl;
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
      });
      if (logoImg) {
        doc.addImage(logoImg, "JPEG", 15, 10, 50, 20);
      }
    } catch (e) {
      console.error("Logo failed to load", e);
    }

    doc.setFontSize(22);
    doc.setTextColor(themeColor[0], themeColor[1], themeColor[2]);
    doc.text("QUOTATION", 195, 25, { align: "right" });

    doc.setDrawColor(themeColor[0], themeColor[1], themeColor[2]);
    doc.setLineWidth(1);
    doc.line(15, 35, 195, 35);

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, "bold");
    doc.text("Quotation Details:", 15, 45);
    doc.setFont(undefined, "normal");
    doc.text(`ID: ${quotation.id}`, 15, 52);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 15, 59);
    doc.text(`Status: ${quotation.status || "Draft"}`, 15, 66);

    doc.setFont(undefined, "bold");
    doc.text("Bill To:", 130, 45);
    doc.setFont(undefined, "normal");
    doc.text(`${quotation.customer_name}`, 130, 52);

    // Table
    const tableRows = (quotation.services || []).map((s) => [
      s.service_name || "-",
      s.quantity.toString(),
      s.unit_price.toFixed(2),
      s.total_price.toFixed(2),
    ]);

    autoTable(doc, {
      startY: 75,
      head: [["Service Name", "Qty", "Unit Price", "Total"]],
      body: tableRows,
      headStyles: { fillStyle: "F", fillColor: themeColor, textColor: 255 },
      alternateRowStyles: { fillColor: [240, 250, 250] },
      margin: { left: 15, right: 15 },
    });

    const finalY = doc.lastAutoTable.finalY + 10;

    doc.setFont(undefined, "bold");
    doc.text(`Subtotal:`, 140, finalY);
    doc.setFont(undefined, "normal");
    doc.text(`${Number(quotation.subtotal || 0).toFixed(2)}`, 195, finalY, { align: "right" });

    let currentY = finalY + 7;
    
    // Check if we have tax or discount in the detail object
    if (quotation.tax_amount > 0) {
      doc.setFont(undefined, "bold");
      doc.text(`Tax:`, 140, currentY);
      doc.setFont(undefined, "normal");
      doc.text(`${Number(quotation.tax_amount).toFixed(2)}`, 195, currentY, { align: "right" });
      currentY += 7;
    }

    doc.setFillColor(themeColor[0], themeColor[1], themeColor[2]);
    doc.rect(135, currentY, 60, 10, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text(`Grand Total:`, 140, currentY + 7);
    doc.text(`${Number(quotation.total_amount || 0).toFixed(2)}`, 190, currentY + 7, { align: "right" });

    // Stamp
    try {
      const stampUrl = process.env.PUBLIC_URL + "/stamp.jpg";
      const stampImg = await new Promise((resolve) => {
        const img = new Image();
        img.src = stampUrl;
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
      });
      if (stampImg) {
        doc.addImage(stampImg, "JPEG", 20, currentY + 20, 30, 30);
      }
    } catch (e) {
      console.error("Stamp failed to load", e);
    }

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text("Authorized Signature", 20, currentY + 55);

    doc.save(`Quotation_${quotation.id}.pdf`);
  };

  if (!quotation) return <div>Loading...</div>;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Quotation #{quotation.id}</h3>
        <button className="btn btn-primary" onClick={handleDownloadPDF}>
          📄 Save as PDF
        </button>
      </div>

      <p><strong>Inquiry ID:</strong> {quotation.inquiry_id}</p>
      <p><strong>Customer:</strong> {quotation.customer_name}</p>
      <p><strong>Status:</strong> {quotation.status}</p>

      <h5>Services</h5>
      <table className="table table-bordered">
        <thead className="table-light">
          <tr>
            <th>Service</th>
            <th>Qty</th>
            <th>Unit</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {quotation.services.map((s) => (
            <tr key={s.id}>
              <td>{s.service_name}</td>
              <td>{s.quantity}</td>
              <td>{s.unit_price.toFixed(2)}</td>
              <td>{s.total_price.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p><strong>Subtotal:</strong> {quotation.subtotal.toFixed(2)}</p>
      <p><strong>Tax:</strong> {quotation.tax_amount.toFixed(2)}</p>
      <p><strong>Total:</strong> {quotation.total_amount.toFixed(2)}</p>

      <Link to="/quotations" className="btn btn-secondary">Back</Link>
    </div>
  );
};

export default QuotationDetail;
